import {useRouter} from 'next/router';
import {type FC, useEffect, useState} from 'react';
import {GSSKT_HOST} from '../../../../config/constants/hosts';
import {LOGIN, MAIL_AUTH_SENT} from '../../../../config/constants/routes';
import {QUERY_PARAMS_FOR_REDIRECT_FROM_LOGIN_PAGE} from '../../../../core/entities/login/constants';
import {
  EMAIL_INPUT_ERROR_TYPE,
  type EmailInputErrorType,
} from '../../../../core/entities/register/constants';
import type {Recruitment} from '../../../../core/repositories/recruitingInfoRepository';
import {usePostRegisterAcMailAuth} from '../../../../core/repositories/registerRepository';
import {resolvePromotionCampaignCode} from '../../../../core/usecases/promotionCampaign/promotionCampaignCode';
import {validateRegisterEmail} from '../../../../shared/services/emailValidation';
import {resolveEntryRegisterInflowPath} from '../../usecase/resolveEntryRegisterInflowPath';
import {EntryRegisterModalPresenter} from './Presenter/EntryRegisterModalPresenter';

type Props = {
  opened: boolean;
  onClose: () => void;
  recruitment: Recruitment;
  entryUrl: string;
  recruitingInfoPage: string;
};

type CastedError = {message: string};

/**
 * 未ログインで一覧のエントリーを押したときに、ログイン画面へ遷移させずその場で会員登録を始めるモーダル。
 * 登録完了後にエントリーへ戻れるよう、inflowPath にエントリーURL（自サイト内のパス）を渡す。
 */
export const EntryRegisterModal: FC<Props> = ({
  opened,
  onClose,
  recruitment,
  entryUrl,
  recruitingInfoPage,
}) => {
  const router = useRouter();
  // 一覧の全カードに置かれるため、初めて開くまでモーダル本体（フォーカストラップやイベント登録）を作らない
  const [hasOpened, setHasOpened] = useState(false);
  useEffect(() => {
    if (opened) {
      setHasOpened(true);
    }
  }, [opened]);
  const [email, setEmail] = useState('');
  const [errorType, setErrorType] = useState<EmailInputErrorType>(
    EMAIL_INPUT_ERROR_TYPE.NONE,
  );

  const inflowPath = resolveEntryRegisterInflowPath({
    entryUrl,
    fallbackPath: recruitingInfoPage,
    siteOrigin: GSSKT_HOST,
  });
  const loginUrl = `${LOGIN}?${QUERY_PARAMS_FOR_REDIRECT_FROM_LOGIN_PAGE}=${encodeURIComponent(inflowPath)}`;

  const {mutate, isLoading} = usePostRegisterAcMailAuth({
    onSuccessFunc() {
      // /mail_auth と同じく送信完了画面でアドレスを見せる。ストレージが使えなくても遷移は止めない
      try {
        sessionStorage.setItem('mailAuthSentEmail', email);
      } catch {}
      location.href = MAIL_AUTH_SENT;
    },
    onErrorFunc(error: unknown) {
      const castedError = error as CastedError;
      setErrorType(
        castedError.message === 'ConflictError'
          ? EMAIL_INPUT_ERROR_TYPE.CONFLICT_ERROR
          : EMAIL_INPUT_ERROR_TYPE.SERVER_ERROR,
      );
    },
  });

  // 送信中は入力と再送信を受け付けない。送信したアドレスと表示・保存するアドレスを一致させるため
  const handleEmailChange = (value: string) => {
    if (isLoading) {
      return;
    }
    setEmail(value);
    setErrorType(EMAIL_INPUT_ERROR_TYPE.NONE);
  };

  const handleSubmit = () => {
    if (isLoading) {
      return;
    }
    const result = validateRegisterEmail(email);
    setErrorType(result);
    if (result !== EMAIL_INPUT_ERROR_TYPE.NONE) {
      return;
    }

    mutate({
      requestBody: {
        email,
        registerType: 0,
        introducerID: '',
        userIntroduceCampaignID: 0,
        inflowPath,
        promotionCampaignCode: resolvePromotionCampaignCode(router.query),
      },
    });
  };

  if (!hasOpened) {
    return null;
  }

  return (
    <EntryRegisterModalPresenter
      opened={opened}
      onClose={onClose}
      recruitmentTitle={recruitment.title}
      schedule={recruitment.schedules[0]}
      loginUrl={loginUrl}
      email={email}
      onEmailChange={handleEmailChange}
      errorType={errorType}
      isSubmitting={isLoading}
      onSubmit={handleSubmit}
    />
  );
};
